import { useEffect } from 'react';
import { useGetClaims } from '../../apiExtensions/account/account';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';
import { useIndexedDB } from '../../hooks/useIndexedDB';
import { useServiceDiscovery } from '../../hooks/useServiceDiscovery';
import { DB_FORM_CACHE_NAME } from '../../utils/FormIndexedDBUtils';
import { clearFormZoomSettingsFromSession } from '../../utils/FormZoomSettings';
import { DB_VERSION } from '../../utils/IndexedDBSchemaRegistry';
import { replaceLocation } from '../../utils/LocationUtils';
import { BFF_LOGOUT_URL_CLAIM_TYPE, getLogoutUrl, LOGIN_URL } from '../../utils/Urls.constant';
import { DB_WHITEPAPERREPORT_NAME } from '../../utils/WhitePaperIndexedDBUtils';

interface SecureRouteProps {
    children: React.ReactNode;
}

const SecureRoute = ({ children }: SecureRouteProps) => {
    const { functionalAccess, isFetchingAccess } = useFunctionalAccess();
    const { homeUrl } = useServiceDiscovery();
    const { deleteDatabase } = useIndexedDB(window.indexedDB, DB_VERSION);
    const { refetch: fetchSessionClaims, isError } = useGetClaims({ query: { retry: false } });

    // If some other piece of changes triggers a re-render
    // we were redirecting to login page multiple times causing issues on IDP
    useEffect(() => {
        if (isError) {
            clearFormZoomSettingsFromSession();
            deleteDatabase(DB_FORM_CACHE_NAME);
            deleteDatabase(DB_WHITEPAPERREPORT_NAME);
            replaceLocation(LOGIN_URL);
        }
    }, [isError]);

    const logoutAndRedirect = async () => {
        clearFormZoomSettingsFromSession();
        try {
            const result = await fetchSessionClaims();
            const logoutUrl = result?.data?.find((c) => c.type === BFF_LOGOUT_URL_CLAIM_TYPE)?.value ?? '';
            if (logoutUrl.length > 1) {
                replaceLocation(getLogoutUrl(logoutUrl));
            } else {
                console.error('Bff logout url not found in session data');
                replaceLocation(LOGIN_URL);
            }
        } catch (err) {
            console.error('Error retrieving Bff logout in session data:', err);
            replaceLocation(LOGIN_URL);
        }
    };

    if (!isFetchingAccess && functionalAccess && !isError) {
        if (!functionalAccess.viewReportsEnabled) {
            if (homeUrl) {
                replaceLocation(homeUrl);
            } else {
                deleteDatabase(DB_FORM_CACHE_NAME);
                deleteDatabase(DB_WHITEPAPERREPORT_NAME);
                logoutAndRedirect();
            }
            return null;
        } else {
            return <>{children}</>;
        }
    }

    return null;
};

export default SecureRoute;
